import React, { useRef, useState } from "react";
import { X, Upload, Download, Loader2, CheckCircle2, AlertCircle, UserPlus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const SAMPLE_CSV = `name,email,password,role,team
Jane Example,jane@yourcompany.com,,employee,Kerry
John Smith,john@yourcompany.com,,employee,West Cork
Alex Boss,alex@yourcompany.com,,admin,Kerry
`;

export default function BulkUploadModal({ open, onClose, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "employees-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/users/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created_count > 0) {
        const sent = data.emails_sent ?? 0;
        toast.success(`Added ${data.created_count} employee${data.created_count > 1 ? "s" : ""} — ${sent} welcome email${sent !== 1 ? "s" : ""} sent`);
      }
      if (data.created_count === 0 && data.error_count === 0) toast.info("No new employees to add — everyone already exists");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="bulk-upload-modal"
    >
      <div
        className="brutal bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 border-2 border-black rounded-xl flex items-center justify-center bg-white hover:bg-neutral-100"
          aria-label="Close"
          data-testid="bulk-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 bg-brand-orange border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
            <UserPlus className="w-5 h-5 text-white" strokeWidth={2.6} />
          </div>
          <div>
            <h3 className="font-display font-black text-xl">Bulk add employees</h3>
            <p className="text-xs text-neutral-600">Upload a .csv or .xlsx with columns: <strong>name</strong>, <strong>email</strong>, password, role, team. Each new employee gets a welcome email with a one-time setup link.</p>
          </div>
        </div>

        {!result && (
          <>
            <div className="border-2 border-dashed border-black rounded-2xl p-6 text-center bg-brand-yellow/30">
              <Upload className="w-8 h-8 mx-auto mb-2" strokeWidth={2.4} />
              <div className="font-semibold">
                {busy ? "Processing…" : "Pick a CSV or XLSX file"}
              </div>
              <div className="text-xs text-neutral-600 mt-1">Up to a few hundred rows. Existing emails are skipped.</div>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                className="btn-brutal btn-primary mt-4"
                data-testid="bulk-pick-file"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {busy ? "Uploading" : "Choose file"}
              </button>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={onFile}
                className="hidden"
                data-testid="bulk-file-input"
              />
            </div>

            <div className="mt-5 border-t-2 border-black/10 pt-4">
              <div className="text-[11px] uppercase tracking-widest font-bold mb-2">Don't have a file yet?</div>
              <button
                type="button"
                onClick={downloadTemplate}
                className="btn-brutal btn-white text-sm"
                data-testid="bulk-download-template"
              >
                <Download className="w-4 h-4" /> Download CSV template
              </button>
              <ul className="text-xs text-neutral-600 mt-4 space-y-1 list-disc list-inside">
                <li><strong>name</strong> and <strong>email</strong> are required.</li>
                <li><strong>password</strong> is optional — leave blank. Each employee will set their own via the welcome email link.</li>
                <li><strong>role</strong> defaults to <code>employee</code> (use <code>admin</code> to create admins).</li>
                <li><strong>team</strong> must be one of: <code>Kerry</code> or <code>West Cork</code> (case-insensitive).</li>
              </ul>
            </div>
          </>
        )}

        {result && (
          <div data-testid="bulk-result">
            <div className="grid grid-cols-3 gap-3">
              <Summary count={result.created_count} label="Created" color="bg-brand-green" Icon={CheckCircle2} />
              <Summary count={result.skipped_count} label="Skipped" color="bg-brand-yellow" Icon={AlertCircle} />
              <Summary count={result.error_count} label="Errors" color="bg-red-200" Icon={AlertCircle} />
            </div>

            {result.created.length > 0 && (
              <ResultSection title="New employees" items={result.created} renderRow={(r) => (
                <>
                  <span className="font-semibold">{r.name}</span>
                  <span className="text-xs text-neutral-600">{r.email}</span>
                  <span className="chip bg-white text-[10px] font-black uppercase">{r.role}</span>
                  {r.team && <span className="chip bg-brand-yellow text-[10px] font-black">{r.team}</span>}
                  <span className={`chip text-[10px] font-black uppercase ${r.welcome_email_sent ? "bg-brand-green" : "bg-red-200"}`}>
                    {r.welcome_email_sent ? "Email sent" : "Email failed"}
                  </span>
                </>
              )} />
            )}
            {result.skipped.length > 0 && (
              <ResultSection title="Skipped (already exist)" items={result.skipped} renderRow={(r) => (
                <>
                  <span className="text-sm">Row {r.row}</span>
                  <span className="text-xs text-neutral-600">{r.email}</span>
                  <span className="text-xs italic text-neutral-500">{r.reason}</span>
                </>
              )} />
            )}
            {result.errors.length > 0 && (
              <ResultSection title="Errors" items={result.errors} color="bg-red-50" renderRow={(r) => (
                <>
                  <span className="text-sm">Row {r.row}</span>
                  <span className="text-xs text-neutral-600">{r.email || "—"}</span>
                  <span className="text-xs italic text-red-700">{r.error}</span>
                </>
              )} />
            )}

            <div className="flex flex-wrap gap-2 mt-5">
              <button onClick={() => setResult(null)} className="btn-brutal btn-white text-sm" data-testid="bulk-upload-more">
                Upload another file
              </button>
              <button onClick={onClose} className="btn-brutal btn-primary text-sm ml-auto" data-testid="bulk-done">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Summary({ count, label, color, Icon }) {
  return (
    <div className={`border-2 border-black rounded-xl p-3 ${color}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" strokeWidth={2.6} />
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="font-display font-black text-2xl">{count}</div>
    </div>
  );
}

function ResultSection({ title, items, renderRow, color = "bg-white" }) {
  return (
    <div className="mt-5">
      <div className="text-[11px] uppercase tracking-widest font-bold mb-2">{title} ({items.length})</div>
      <div className={`border-2 border-black rounded-xl divide-y-2 divide-black/10 max-h-40 overflow-y-auto ${color}`}>
        {items.map((r, i) => (
          <div key={i} className="p-2 flex flex-wrap items-center gap-2">
            {renderRow(r)}
          </div>
        ))}
      </div>
    </div>
  );
}
